import { fromEvent, merge } from 'rxjs';
import {
    startWith,
    map,
    distinctUntilChanged,
    switchMap,
} from 'rxjs/operators';
import { getToken, redirectToAuth } from './http.js';
import { createMainView } from './main-view.js';
import { createWriteView } from './write-view.js';
import { createEditView } from './edit-view.js';
import { createStudentsView } from './students-view.js';
import { createTopicsView } from './topics-view.js';
import { createSessionSetupView } from './session-setup-view.js';
import { createSessionsView } from './sessions-view.js';
import { createCardsView } from './cards-view.js';
import { createTtsView } from './tts-view.js';
import { showError } from './ui.js';

// Redirect to auth immediately if no session token is present
if (!getToken()) {
    redirectToAuth();
}

/**
 * Maps a hash path string and optional query string to a view Observable.
 * Unknown routes fall back to the main view.
 */
function matchView(path, qs) {
    if (path === 'write') {
        return createWriteView();
    }
    if (path === 'cards') {
        let params = new URLSearchParams(qs);
        return createCardsView({
            topicId: params.get('topic_id') || '',
            subjectId: params.get('subject_id') || '',
            grundschuleClass: params.get('grundschule_class') || '',
            gymnasiumClass: params.get('gymnasium_class') || '',
        });
    }
    if (path === 'students') {
        return createStudentsView();
    }
    if (path === 'topics') {
        return createTopicsView();
    }
    if (path === 'sessions') {
        return createSessionsView();
    }
    if (path === 'session-setup') {
        return createSessionSetupView();
    }
    if (path === 'tts') {
        return createTtsView();
    }
    let editMatch = path.match(/^edit\/(\d+)$/);
    if (editMatch) {
        return createEditView(parseInt(editMatch[1], 10));
    }
    return createMainView();
}

// Hash-based router — fires immediately on page load via startWith,
// then on every subsequent hashchange. Splits the hash into a path and an
// optional query string so that routes like #cards?topic_id=3 are supported.
let router$ = fromEvent(window, 'hashchange').pipe(
    startWith(undefined),
    map(() => {
        let hash = window.location.hash.replace(/^#/, '');
        let [path, qs] = hash.split('?');
        return { path, qs: qs || '' };
    }),
    distinctUntilChanged((a, b) => a.path === b.path && a.qs === b.qs),
    switchMap(({ path, qs }) => matchView(path, qs))
);

merge(router$).subscribe({
    error: (err) => showError(err),
});
